import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { v4 } from 'uuid';
import {
  taskValidationSchema,
  mapIconSelector,
  TASK_FIELDS,
} from 'utils/formFields/task';
import {
  getTaskLocationMarker,
  transformLocationInParent,
} from 'utils/helper/task';
import useSites from 'containers/Pages/Locations/Sites/Layouts/useSite.js';
import TaskForm from '../index';
import { detailedSection, cloneTaskInitialValues } from '../options';
import { generalSection } from './options';
import { transformDefaultSubTask } from 'utils/DataTransform/Task/taskForm';
import { GEOGRAPHICAL_MAP, EVENT_MAP_ICON_TYPE } from 'utils/constants/Map';
import { sitesMapConfig } from 'utils/constants/sites.js';
import { isEmpty, get } from 'lodash';
import API from 'services/api';
import { transform as transformSiteDetails } from 'utils/DataTransform/Sites/SiteDetails';
import FormDataPopulation from 'components/Forms/Fields/FormDataPopulation/index';
import { convertPercentageToimageCordinates } from 'utils/DataTransform/CleanerTask/CleanerTaskDetails';
import { SUBTASK_DEFAULT, TASK_TYPE } from 'utils/constants/task';
import { createError } from 'reducers/ui';
import { errorHandler } from 'utils/errorMessages';
import { validationObject, validateRequired } from 'utils/validations';
import { useSelector } from 'react-redux';
import { getGlobalSiteList } from 'selectors/globalSelectors';
import { useTranslation } from 'react-i18next';

const SubTaskForm = ({
  // id,
  closeForm,
  editMode,
  defaultValues = {}, // this object has selectedParentTask
  users,
  formProps, // parent form values
  formItem, // sub task component defined values from options
  editModeValues,
  hideAssignee,
  omitFields = { generalSection: [], detailedSection: [] },
  multiSchedule,
  siteEnabled,
}) => {
  const sites = useSites();
  const { t } = useTranslation();
  const siteList = useSelector(state => getGlobalSiteList(state));
  const [showPolylines, setShowPolylines] = useState(false);
  const [surfaceLocation, setSurfaceLocation] = useState([]);
  const [surfaceImageId, setSurfaceImageId] = useState(GEOGRAPHICAL_MAP.id);
  const [location, setLocation] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [selectedSiteLocation, setSiteLocation] = useState([]);
  const [center, setCenter] = useState(null);
  const [path, setPath] = useState();
  const [selectedSiteDetailImages, setSelectedSiteDetailImages] = useState();
  const [resetValues, setResetValues] = useState();
  const [resetForm, setResetForm] = useState(false);

  useEffect(() => {
    if (siteId !== '') {
      const fetchSiteDetails = async id => {
        try {
          const site = await API.fetchSiteDetails(id);
          if (site.data.data != null) {
            const siteDetails = transformSiteDetails({
              data: site.data.data,
              visitors: [],
            });
            if (siteDetails) {
              const { detailedInfo } = siteDetails;
              if (detailedInfo) {
                const images = detailedInfo.surfaces.value;
                setSelectedSiteDetailImages(images);
              }
            }
          }
        } catch (err) {
          console.log(err);
        }
        return null;
      };

      fetchSiteDetails(siteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const onCreate = values => {
    const currentSubTaskValue = formProps.values[formItem.field] || [];
    const currentValues = {
      ...values,
      id: values.id || v4(),
    };

    if (values.location) {
      const location = transformLocationInParent({
        formItem,
        formProps,
        childFormValue: currentValues,
        defaultIndex: formProps.values[formItem.field]
          ? formProps.values[formItem.field].length + 1
          : 1,
      });
      if (
        location?.additionalMarkers &&
        location.additionalMarkers.length > 0
      ) {
        // if parent surface id is GEOGRAPHICAL_MAP.id, then get all points of type GEOGRAPHICAL_MAP
        if (formProps?.values?.siteSurfaceImages.id === GEOGRAPHICAL_MAP.id) {
          location.additionalMarkers = location.additionalMarkers.filter(
            marker => marker.surfaceUuid === GEOGRAPHICAL_MAP.id,
          );
        } else {
          location.additionalMarkers = location.additionalMarkers
            ? location.additionalMarkers.filter(
                marker =>
                  marker.surfaceUuid === formProps.values.siteSurfaceImages.id,
              )
            : [];
        }
        // additional markers is an array of location of subtasks
        formProps.setFieldValue(
          'additionalMarkers',
          location.additionalMarkers,
        );
      }

      currentValues.location = [
        {
          ...currentValues.location[0],
          text: location.text,
          seqNumber: location.seqNumber,
        },
      ];
    }

    formProps.setFieldValue(formItem.field, [
      ...currentSubTaskValue,
      currentValues,
    ]);
    closeForm();
  };

  const onEdit = values => {
    // current stored sub task values
    const currentSubTaskValue = formProps.values[formItem.field] || [];
    // here the current stored sub task values are updated by current values
    const updatedSubTask = currentSubTaskValue.map(item => {
      return item.id === values.id ? { ...item, ...values } : item;
    });

    if (values.location) {
      const location = transformLocationInParent({
        formItem,
        formProps,
        childFormValue: values,
        editModeValues,
      });

      values.location = [
        {
          ...values.location[0],
          text: location.text,
          seqNumber: location.seqNumber,
        },
      ];

      if (
        location?.additionalMarkers &&
        location.additionalMarkers.length > 0
      ) {
        // if parent surface id is GEOGRAPHICAL_MAP.id, then get all points of type GEOGRAPHICAL_MAP
        if (formProps?.values?.siteSurfaceImages.id === GEOGRAPHICAL_MAP.id) {
          location.additionalMarkers = location.additionalMarkers.filter(
            marker => marker.surfaceUuid === GEOGRAPHICAL_MAP.id,
          );
        } else {
          location.additionalMarkers = location.additionalMarkers
            ? location.additionalMarkers.filter(
                marker =>
                  marker.surfaceUuid === formProps.values.siteSurfaceImages.id,
              )
            : [];
        }
        formProps.setFieldValue(
          'additionalMarkers',
          location.additionalMarkers,
        );
      }
    }

    formProps.setFieldValue(formItem.field, updatedSubTask);
    closeForm();
  };

  const onChange = values => {
    setShowPolylines(values.subtasksSequentialOrder);
    if (values?.taskType) {
      values.taskType = formProps.values?.taskType;
    }

    if (
      !isEmpty(values?.location) &&
      (values.taskType === '' || values.taskType === undefined)
    ) {
      values.location[0] = {
        ...values.location[0],
        icon: EVENT_MAP_ICON_TYPE.NON_NEWS_EVENT_SINGLE_LOC,
      };
    }
    if (!isEmpty(values.location) && formProps.values?.taskType) {
      values.location[0] = {
        ...values.location[0],
        icon: formProps.values.taskType,
      };
    }

    if (values.additionalMarkers && values.additionalMarkers.length > 0) {
      values.additionalMarkers = values.additionalMarkers.map(item => ({
        ...getTaskLocationMarker({
          id: item.id,
          showPolylines: values.subtasksSequentialOrder,
          lat: item.lat,
          lng: item.lng,
          text: item.text,
          seqNumber: item.seqNumber,
          surfaceUuid: item.surfaceUuid,
          icon: mapIconSelector(formProps.values?.taskType),
          subtaskId: item.subtaskId,
        }),
      }));
    }
    if (values.siteSurfaceImages && values.siteSurfaceImages !== '') {
      setSurfaceImageId(values.siteSurfaceImages.id);
    } else {
      setSurfaceImageId(GEOGRAPHICAL_MAP.id);
    }
    if (values.location) {
      setSurfaceLocation(values.location);
    }
    if (values.site && values.site?.id) {
      let selectedSite = '';
      if (typeof values.site === 'string') {
        selectedSite = sites.find(site => site.id === values.site);
      } else {
        selectedSite = sites.find(site => site.id === values.site.id);
      }
      setSiteId(selectedSite.id);
      setSiteLocation(selectedSite);

      // incase of the geographical map we need centerOfGravity to point to the location

      if (!values.center || values.location) {
        const latitude = selectedSite?.centreOfGravity?.latitude;
        const longitude = selectedSite?.centreOfGravity?.longitude;
        if (!center) {
          setCenter({
            lat: latitude,
            lng: longitude,
          });
        } else {
          const latitude = selectedSite?.centreOfGravity?.latitude;
          const longitude = selectedSite?.centreOfGravity?.longitude;
          if (latitude !== center.lat && longitude !== center.lng) {
            setCenter({
              lat: selectedSite.centreOfGravity.latitude,
              lng: selectedSite.centreOfGravity.longitude,
            });
          }
        }
      }
    } else {
      setSiteId('');
      setSelectedSiteDetailImages();
    }
    if (values.siteLocations && values.siteLocations.length > 0) {
      const sitePaths = values.siteLocations[0].paths;
      if (sitePaths && sitePaths.length > 2) {
        setPath(sitePaths);
      }
    }
    if (values.location) {
      setLocation(values.location);
    }

    if (
      values?.additionalMarkers &&
      values.additionalMarkers.length > 0 &&
      values?.siteSurfaceImages
    ) {
      if (values?.siteSurfaceImages?.id === GEOGRAPHICAL_MAP.id) {
        values.additionalMarkers = values.additionalMarkers.filter(
          marker => marker.surfaceUuid === GEOGRAPHICAL_MAP.id,
        );
      } else {
        values.additionalMarkers = values.additionalMarkers
          ? values.additionalMarkers.filter(
              marker => marker.surfaceUuid === values.siteSurfaceImages.id,
            )
          : undefined;
      }
    }
  };

  const onChangeCallback = async ({ values, details }) => {
    try {
      const { data } = isEmpty(details)
        ? await API.fetchTaskDetails({
            id: values.copyFromTemplate.id,
          })
        : details;

      // eslint-disable-next-line prefer-const
      let { tasks, joins, surfaces } = data.data;
      // change the coordinates back to x and y from percentages
      joins = convertPercentageToimageCordinates(joins, surfaces);
      tasks = convertPercentageToimageCordinates(tasks, surfaces);

      // eslint-disable-next-line no-unused-vars
      const { id, ...newFormInitial } = cloneTaskInitialValues({
        taskToCloneId: values.copyFromTemplate.id,
        taskToCloneDetails: { tasks, joins, surfaces },
        defaultValues,
        subTaskDefaultValues: SUBTASK_DEFAULT,
        allowSubTask: true,
        siteList,
      });
      // scancode should be null when copying from a template
      const setScanCodeValues = {
        ...newFormInitial,
        scanCode: null,
        scanStartOnly: null,
        taskName: `Copy - ${newFormInitial.taskName}`,
      };
      setResetValues(setScanCodeValues);
      setResetForm(true);
    } catch (e) {
      createError(errorHandler(e));
    }
  };

  const headerContent = editMode => {
    if (!editMode) {
      return (
        <FormDataPopulation
          formHeader="Select Template to Copy"
          buttonText="Copy Existing Template"
          submitCallback={onChangeCallback}
          subFields={[
            TASK_FIELDS.template({ taskType: TASK_TYPE.CLEANING.id }),
          ]}
          validationSchema={validationObject({
            copyFromTemplate: validateRequired().nullable(),
          })}
          initialValues={{ copyFromTemplate: null }}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <TaskForm
      headerContent={headerContent(editMode)}
      onChange={onChange}
      editMode={editMode}
      initialValues={transformDefaultSubTask({
        defaultValues: editMode
          ? { ...defaultValues, ...editModeValues }
          : defaultValues,
        values: formProps.values,
        editModeValues,
        surfaceImageId,
      })}
      formTitle={
        editMode
          ? `${t('Edit Subtask')} ${
              formProps.values.taskName ? `of ${formProps.values.taskName}` : ''
            }`
          : `${t('Create Subtask')} ${
              formProps.values.taskName ? `of ${formProps.values.taskName}` : ''
            }`
      }
      saveHoverText={t('Save Subtask')}
      closeForm={closeForm}
      onEdit={onEdit}
      onCreate={onCreate}
      validationSchema={taskValidationSchema({
        isSubtask: true,
        requiredTaskTypeValidation: false, // subtasks are not required to have taskType validations
      })}
      resetForm={resetForm}
      setResetForm={setResetForm}
      resetValues={resetValues}
      generalSection={generalSection({
        users,
        sites,
        hideAssignee,
        omitFields: omitFields.generalSection,
        location,
        siteEnabled,
      })}
      detailedSection={detailedSection({
        location,
        surfaceLocation,
        surfaceImageId,
        selectedSiteDetailImages,
        taskDetails: { ...defaultValues, ...editModeValues },
        allowSubTask: true,
        displaySequence: formProps.values[formItem.field],
        users,
        subtaskDefaultValues: defaultValues,
        columns: formItem.columns,
        center:
          selectedSiteLocation.length > 0
            ? {
                lat: selectedSiteLocation.centreOfGravity.latitude,
                lng: selectedSiteLocation.centreOfGravity.longitude,
              }
            : null,
        showPolylines,
        hideAssignee,
        omitFields: omitFields.detailedSection,
        subtaskOmitFields: omitFields,
        multiSchedule,
        siteLocation: !isEmpty(path)
          ? [
              {
                ...sitesMapConfig,
                paths: path.map(point => ({
                  lat: point.lat,
                  lng: point.lng,
                })),
              },
            ]
          : get(selectedSiteLocation, 'locationDetail', []),
      })}
    />
  );
};

SubTaskForm.propTypes = {
  id: PropTypes.string,
  editModeValues: PropTypes.object,
  closeForm: PropTypes.func,
  editMode: PropTypes.bool,
  taskDetails: PropTypes.object,
  defaultValues: PropTypes.object,
  assigneeList: PropTypes.array,
  siteEnabled: PropTypes.array,
  allowSubTask: PropTypes.bool,
  formProps: PropTypes.object,
  formItem: PropTypes.object,
  hideAssignee: PropTypes.bool,
  omitFields: PropTypes.object,
  users: PropTypes.object,
  multiSchedule: PropTypes.bool,
};

export default SubTaskForm;
